import type { Pixel } from "@pdfking/math"
import type { PdfEngine } from "../types/engine/PdfEngine"
import type { TaskResult } from "../types/tasks/input/TaskResult"
import type { TaskRasterizeOptions } from "../types/tasks/options/TaskRasterizeOptions"
import type { TrackingCallback } from "../types/tasks/tracking/TrackingCallback"
import { PDFRasterize } from "@pdfking/renderer-default"
import { First, Range } from "@pdfking/tfunc"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { ConvertPixels } from "../geometry/ConvertPixels"
import { ContentType } from "../types/files/ContentType"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TaskName } from "../types/tasks/TaskName"
import { TrackedTaskName } from "../types/tasks/tracking/TrackedTaskName"

const singlePage = (page_count: number) => {
  if (page_count !== 1) {
    throw new Error(`Expected 1 page, got ${page_count}`)
  }
  return Range(1, 1)
}

export async function* TaskRasterize(
  { options: { image_dpi, unit_to_px }, source }: TaskRasterizeOptions,
  emit: TrackingCallback,
  engine: PdfEngine
): AsyncGenerator<TaskResult> {
  void emit({
    done: 0,
    name: TrackedTaskName.RASTERIZE_PDF,
    total: 1,
  })
  const { size, stream } = await RequestBlob(engine, source, emit)
  const input = { size, stream, type: ContentType.PDF }

  const result = await First(PDFRasterize(input, image_dpi, singlePage))
  const content = await GenerateFile(
    engine,
    [result.blob],
    ContentType.PNG,
    emit
  )

  yield {
    content: {
      enum: VirtualFileType.SINGLE_PAGE_IMAGE,
      content,
      dpi: image_dpi,
      lpi: 0 as Pixel,
      size: ConvertPixels(
        { unit_to_px },
        { height: result.height, width: result.width }
      ),
      source,
      file_meta: {},
    },
    enum: TaskName.CONTENT_FILE,
    name: "img.png",
  } as TaskResult

  void emit({
    done: 1,
    name: TrackedTaskName.RASTERIZE_PDF,
    total: 1,
  })
}
